import clsx from "clsx";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { debounce } from "lodash";

import Paper from "components/common/Paper";
import SpaceListHeader from "./SpaceListHeader";
import SearchTextField from "components/common/InputField/SearchTextField";
import SpaceListItem from "./SpaceListItem";
import SpaceListItemWithUnreadCount from "./SpaceListItemWithUnreadCount";
import SpaceEmptyContent from "./SpaceEmptyContent";
import useToggle from "hooks/useToggle";
import SpaceAddEditModal from "./SpaceAddEditModal";
import { useGetSpaces } from "hooks/spaceHooks";
import LoadingContent from "components/common/LoadingContent";
import Skeleton from "react-loading-skeleton";
import { SpaceFilters } from "redux/Ldb/types/spaceTypes";
import Loader from "components/common/Loader";
import { getFromStorage } from "helpers";
import * as UrlConstants from "constants/urls";

type SpaceListProps = {
    expandConversation: boolean;
    isGuest: boolean;
    handleToggleExpandConversation: () => void;
};

export default function SpaceList(props: SpaceListProps) {
    const { expandConversation, isGuest } = props;
    const navigate = useNavigate();
    const userProfile = getFromStorage("ally-user");

    const brandId = userProfile?.user?.assignedBrandsId?.[0] || userProfile?.user?.profile?._id;

    const [spaceAddEditModal, toggleSpaceAddEditModal] = useToggle();

    const [filterParams, setFilterParams] = useState<SpaceFilters>({
        search: "",
        archived: false,
        sortDirection: "desc",
        ...(isGuest ? { brand: brandId } : {}),
    });

    const [debouncedSearch, setDebouncedSearch] = useState<string>("");
    const { spaceId } = useParams();

    const getAllSpacesQuery = useGetSpaces(
        useMemo(
            () => ({
                archived: filterParams.archived,
                sortDirection: filterParams.sortDirection,
                search: debouncedSearch,
                ...(isGuest ? { brand: filterParams.brand } : {}),
            }),
            [
                filterParams.sortDirection,
                filterParams.archived,
                debouncedSearch,
                filterParams.brand,
                isGuest,
            ]
        ),
        {
            isInfiniteScroll: true,
            scrollContentId: "space-list-container",
        }
    );

    const isEmpty = getAllSpacesQuery?.data?.length === 0;

    const handleChangeSearch = (e) => {
        const value = e.target.value;
        setFilterParams((prev) => ({
            ...prev,
            search: value,
        }));
    };

    const handleChangeArchived = (value: boolean) => {
        setFilterParams((prev) => ({
            ...prev,
            archived: value,
        }));
    };

    const handleChangeSortDirection = (value: "asc" | "desc") => {
        setFilterParams((prev) => ({
            ...prev,
            sortDirection: value,
        }));
    };

    const handleChangeAll = () => {
        setFilterParams((prev) => ({
            ...prev,
            archived: false,
            sortDirection: "desc",
        }));
    };

    useEffect(() => {
        if (!spaceId && getAllSpacesQuery?.data?.length > 0) {
            navigate(
                generatePath(
                    isGuest ? UrlConstants.SPACES_GUEST_DETAILS : UrlConstants.SPACES_DETAILS,
                    {
                        spaceId: getAllSpacesQuery?.data[0]?._id,
                    }
                )
            );
        }
    }, [getAllSpacesQuery?.data]);

    return (
        <Paper
            className={clsx(
                expandConversation ? "w-0 border-none shadow-none" : "w-full w-[25%]",
                "h-full transition-all duration-300 ease-in-out !p-[0px] flex flex-col"
            )}
        >
            {!expandConversation && (
                <>
                    <SpaceListHeader
                        {...{
                            isGuest,
                            handleChangeArchived,
                            handleChangeSortDirection,
                            handleChangeAll,
                        }}
                    />
                    <SearchTextField
                        value={filterParams.search}
                        onChange={handleChangeSearch}
                        onInput={debounce((e) => {
                            setDebouncedSearch(e.target.value);
                        }, 1000)}
                        inputPlaceholder="Search"
                        inputContainerClassname="border-b !border-t-0 !border-l-0 !border-r-0 !border-slate-100 !rounded-none !z-0 !overflow-hidden"
                    />

                    <div
                        className="py-[12px] h-full px-[16px] overflow-y-scroll scrollbar-hidden"
                        id="space-list-container"
                    >
                        <LoadingContent
                            loading={getAllSpacesQuery.isLoading}
                            renderLoading={() => (
                                <div className="flex flex-col gap-1">
                                    {Array.from({ length: 5 }).map((_, index) => (
                                        <Skeleton
                                            key={index}
                                            height={100}
                                            width={isGuest ? 266.7 : 300.78}
                                            borderRadius={8}
                                        />
                                    ))}
                                </div>
                            )}
                        >
                            {isEmpty && !getAllSpacesQuery.isFetching ? (
                                <SpaceEmptyContent
                                    {...{
                                        ...(isEmpty && debouncedSearch === ""
                                            ? {
                                                  title: "No spaces yet",
                                                  description:
                                                      "Spaces help you organize projects, products, and conversations into one place. Create your first space to get started.",
                                                  action: toggleSpaceAddEditModal,
                                                  ...(isGuest
                                                      ? {}
                                                      : { actionText: "Create a Space" }),
                                              }
                                            : {
                                                  title: "No spaces found",
                                                  description:
                                                      "No spaces found with the search query",
                                              }),
                                    }}
                                    icon="ri-chat-new-line"
                                />
                            ) : (
                                <Fragment>
                                    {getAllSpacesQuery.data.map((item, index) => (
                                        <SpaceListItemWithUnreadCount
                                            key={index}
                                            item={item}
                                            index={index}
                                            spaceId={spaceId || ""}
                                            refreshKey={0}
                                        />
                                    ))}
                                </Fragment>
                            )}
                        </LoadingContent>

                        {getAllSpacesQuery?.isFetching && (
                            <div className="flex justify-center items-center">
                                <Loader />
                            </div>
                        )}
                    </div>
                </>
            )}

            {spaceAddEditModal && (
                <SpaceAddEditModal
                    open={spaceAddEditModal}
                    closeModal={() => toggleSpaceAddEditModal()}
                />
            )}
        </Paper>
    );
}
